import { useContext, useEffect, useState } from 'react';
import { SessionstoragePersistence } from '@updater/ui-utilities';
import { InternetUsage } from '@updater/ui-widgets';
import { MicrositeContext } from 'context';
import { merchandisingSchema } from 'components/QuizWrapper/informant/schema/schema';
import { stripUi } from '@updater/ui-informant';
import { QuestionForm } from 'types/QuestionForm';

const ANSWER_VALUES = new Set<string>(Object.values(InternetUsage));

function toInternetUsage(answers: unknown): InternetUsage[] {
  if (!Array.isArray(answers)) return [];

  return answers.filter(
    (answer): answer is InternetUsage =>
      typeof answer === 'string' && ANSWER_VALUES.has(answer)
  );
}

/**
 * The visitor's "what do you use the internet for?" answers, which decide which
 * toppers the showcase personalizes.
 *
 * Reads the quiz's own answer vocabulary. A retired quiz stored a different
 * vocabulary under `activities`; nothing writes that any more (COREX-1934), so
 * no translation is needed.
 *
 * Must stripUi() before hashing (FormProvider does the same) or it lands on the
 * wrong sessionStorage key.
 */
export function useQuizInternetUsage(): InternetUsage[] | undefined {
  const { micrositeCode } = useContext(MicrositeContext);
  // Undefined until the stored answers are read, so callers can tell "not
  // read yet" from "the quiz was skipped" (an empty array).
  const [internetUsage, setInternetUsage] = useState<
    InternetUsage[] | undefined
  >(undefined);

  useEffect(() => {
    let isMounted = true;

    SessionstoragePersistence(micrositeCode)
      .getOrCreateEntry(stripUi(merchandisingSchema))
      .then(({ data }) => {
        if (!isMounted) return;
        setInternetUsage(
          toInternetUsage((data as QuestionForm | null)?.internetUsage)
        );
      });

    return () => {
      isMounted = false;
    };
  }, [micrositeCode]);

  return internetUsage;
}
